/**
 * cart-store-availability-drawer.js
 *
 * Uses the Storefront GraphQL API (same as variant-picker-tabs.js) to fetch
 * storeAvailability for each cart variant in parallel.
 *
 * Flow:
 *  1. Drawer opens → fetch storeAvailability for each variant (parallel, cached).
 *  2. Show all unique store locations (name, address, phone).
 *  3. Click a store row to highlight it.
 *  4. Click "Select Store" → update the pickup line in each cart item on the page.
 */

(function (global) {
  'use strict';

  var _cartItems      = [];
  var _fetchCache     = {};
  var _enrichedItems  = [];
  var _selectedStore  = null;
  var _storeConfirmed = false;
  var _shopDomain    = (window.Shopify && window.Shopify.shop) ? window.Shopify.shop : window.location.hostname;
  var _storefrontToken = null;
  var _shipIds       = new Set(); // numeric location IDs treated as Online (shipping)
  var _pickupCounted = true;      // pickup-only stock counted in online inventory
  var _iconAvailable   = '';
  var _iconUnavailable = '';
  var _strAllAvailable   = 'All items are available';
  var _strSomeUnavailable = '1 or more items are not available';
  var _strLoading        = 'Loading store availability...';
  var _strNoStores       = 'No stores available for pickup.';
  var _strNotAvailable   = '– Not available at this store';
  var _strAvailablePickup    = 'Available for Pickup';
  var _strNotAvailablePickup = 'Not Available for Pickup';
  var _strAvailableDelivery    = 'Available for Delivery';
  var _strNotAvailableDelivery = 'Not Available for Delivery';

  /* Store currently rendered on the cart rows. Only ever set from the shopper's
     own confirmed choice in the drawer — there is no automatic fallback, so the
     pickup half of the strip stays collapsed until they actually pick a store. */
  var _activeStore     = null;
  var _defaultStoreRaw = '';    // theme setting: numeric Location ID or store name
                                // (drawer pre-highlight only — never auto-renders)
  var _pickupEnabled   = true;  // settings.cart_pickup_row_enabled kill switch
  var _updateTimer     = null;  // cart:updated debounce
  var _LS_KEY          = 'sixty8ight:cart:pickupStore:v1';

  /* Availability at a single store. Mirrors the PDP in-store rule
     (variant-picker-tabs.js buildStores): in stock when qty > 0 OR available. */
  function _isAvailableAt(entry) {
    return !!(entry && (entry.qty > 0 || entry.available));
  }

  /* GID -> numeric id ("gid://shopify/Location/123" -> "123"). */
  function _numericId(gid) {
    return String(gid || '').split('/').pop();
  }

  /* Per-variant Online (delivery) / In-store (pickup) flags.
     Mirrors variant-picker-tabs.js buildAvailabilityMap so the cart and PDP agree.
     `nodes` are the raw storeAvailability nodes; `qa` is variant.quantityAvailable
     (may be null when untracked); `forSale` is variant.availableForSale. */
  function _computeFulfillment(nodes, qa, forSale) {
    var Q = function (n) { return Math.max(0, (n.quantityAvailable != null ? n.quantityAvailable : 0)); };

    var shipPickupQty = 0; // stock at shipping locations
    var pickupOnlyQty = 0; // stock at pickup-only locations
    nodes.forEach(function (n) {
      var id = _numericId(n.location && n.location.id);
      if (_shipIds.has(id)) shipPickupQty += Q(n);
      else pickupOnlyQty += Q(n);
    });

    // In-store: any location has stock (per-location, exact).
    var instore = nodes.some(function (n) { return Q(n) > 0 || n.available; });

    // Online: shipping-location stock, with aggregate fallbacks (see PDP dev plan §5).
    var online;
    if (_shipIds.size === 0)      online = !!forSale;              // not configured
    else if (qa == null)          online = !!forSale;              // untracked inventory
    else if (shipPickupQty > 0)   online = true;                   // ships & has stock
    else                          online = (qa - (_pickupCounted ? pickupOnlyQty : 0)) > 0;

    return { online: online, instore: instore };
  }

  function _esc(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ─── Load cart items + token from baked HTML ────────────────────────────── */
  function _loadCartItems() {
    var el = document.getElementById('store-avail-data');
    if (el) {
      try { _cartItems = JSON.parse(el.textContent || '[]'); } catch (e) {
        console.error('[StoreAvail] Failed to parse cart items:', e);
      }
    }
    var drawer = document.getElementById('store-avail-drawer');
    if (drawer) {
      _storefrontToken = drawer.dataset.storefrontToken || null;
      _shipIds = new Set(
        (drawer.dataset.shippingLocationIds || '')
          .split(',')
          .map(function (s) { return s.trim(); })
          .filter(Boolean)
      );
      _pickupCounted = drawer.dataset.pickupCounted === 'true';
      _defaultStoreRaw = (drawer.dataset.defaultPickupStore || '').trim();
      // Absent attribute (older cached markup) defaults to enabled.
      _pickupEnabled = drawer.dataset.pickupRowEnabled !== 'false';
    }

    var availEl   = document.getElementById('store-avail-icon-available');
    var unavailEl = document.getElementById('store-avail-icon-unavailable');
    if (availEl)   _iconAvailable   = availEl.innerHTML;
    if (unavailEl) _iconUnavailable = unavailEl.innerHTML;

    if (drawer && drawer.dataset.strAllAvailable)   _strAllAvailable   = drawer.dataset.strAllAvailable;
    if (drawer && drawer.dataset.strSomeUnavailable) _strSomeUnavailable = drawer.dataset.strSomeUnavailable;
    if (drawer && drawer.dataset.strLoading)        _strLoading        = drawer.dataset.strLoading;
    if (drawer && drawer.dataset.strNoStores)       _strNoStores       = drawer.dataset.strNoStores;
    if (drawer && drawer.dataset.strNotAvailable)   _strNotAvailable   = drawer.dataset.strNotAvailable;
    if (drawer && drawer.dataset.strAvailablePickup)    _strAvailablePickup    = drawer.dataset.strAvailablePickup;
    if (drawer && drawer.dataset.strNotAvailablePickup) _strNotAvailablePickup = drawer.dataset.strNotAvailablePickup;
    if (drawer && drawer.dataset.strAvailableDelivery)    _strAvailableDelivery    = drawer.dataset.strAvailableDelivery;
    if (drawer && drawer.dataset.strNotAvailableDelivery) _strNotAvailableDelivery = drawer.dataset.strNotAvailableDelivery;

    console.log('[StoreAvail] Cart items:', _cartItems);
    console.log('[StoreAvail] Token:', _storefrontToken ? '✓' : '✗ missing');
  }

  /* ─── Working variant list ───────────────────────────────────────────────────
     Derived from the rendered rows, NOT from /cart.js. Two reasons:
       - /cart.js returns bundle child line items, which cart-items.liquid skips
         rendering (see its `continue` on item.parent_relationship.parent.key), so
         they have no row to update and must not be fetched.
       - Two lines can share one variant id (e.g. a normal line plus a redeemed
         one); the ids must be deduped before they go into the GraphQL query.
     Falls back to the baked JSON when there are no cart-page rows (e.g. the
     drawer opened from a context that doesn't render them). */
  function _domVariantIds() {
    var ids = [];
    var seen = {};
    document.querySelectorAll('.cart-page-items .cart-item[data-variant-id]')
      .forEach(function (row) {
        var id = String(row.dataset.variantId || '');
        if (id && !seen[id]) { seen[id] = true; ids.push(id); }
      });
    return ids;
  }

  function _workingVariantIds() {
    var ids = _domVariantIds();
    if (ids.length) return ids;
    var seen = {};
    return _cartItems
      .map(function (i) { return String(i.variantId); })
      .filter(function (id) {
        if (!id || seen[id]) return false;
        seen[id] = true;
        return true;
      });
  }

  /* ─── Batch prefetch ─────────────────────────────────────────────────────────
     One `nodes(ids: [...])` request for every variant not already in _fetchCache,
     writing results into that same cache. Because _fetchStores() returns early on
     a cache hit, its callers need no changes — after this runs they resolve
     synchronously. Turns an N-request cart load into a single request. */
  var _batchQuery = [
    'query cartStoreAvailability($ids: [ID!]!) {',
    '  nodes(ids: $ids) {',
    '    ... on ProductVariant {',
    '      id',
    '      availableForSale',
    '      quantityAvailable',
    '      storeAvailability(first: 50) {',
    '        edges { node {',
    '          available',
    '          quantityAvailable',
    '          location { id name address { address1 city phone } }',
    '        } }',
    '      }',
    '    }',
    '  }',
    '}'
  ].join(' ');

  /* Raw GraphQL variant node -> the { stores, online, instore, ok } shape that
     _fetchStores produces and every consumer downstream expects. */
  function _normaliseVariantNode(node) {
    var edges = (node.storeAvailability && node.storeAvailability.edges) || [];
    var nodes = edges.map(function (edge) { return edge.node; });
    var stores = nodes.map(function (n) {
      return {
        id:        _numericId(n.location && n.location.id),
        name:      n.location.name,
        available: n.available,
        qty:       n.quantityAvailable || 0,
        address1:  (n.location.address && n.location.address.address1) || '',
        city:      (n.location.address && n.location.address.city)     || '',
        phone:     (n.location.address && n.location.address.phone)    || ''
      };
    });
    var flags = _computeFulfillment(nodes, node.quantityAvailable, node.availableForSale);
    return { stores: stores, online: flags.online, instore: flags.instore, ok: true };
  }

  function _fetchAllStores(ids) {
    var missing = (ids || []).filter(function (id) { return !_fetchCache[id]; });
    if (!missing.length) return Promise.resolve();

    if (!_storefrontToken) {
      console.error('[StoreAvail] No storefront token — pickup unavailable');
      return Promise.resolve();
    }

    var gids = missing.map(function (id) { return 'gid://shopify/ProductVariant/' + id; });
    console.log('[StoreAvail] Batch fetching', missing.length, 'variant(s)');

    return fetch('/api/2024-10/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': _storefrontToken
      },
      body: JSON.stringify({ query: _batchQuery, variables: { ids: gids } })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('Storefront HTTP ' + r.status);
        return r.json();
      })
      .then(function (json) {
        if (json.errors && json.errors.length) {
          console.warn('[StoreAvail] GraphQL errors:', json.errors);
        }
        var nodes = (json.data && json.data.nodes) || [];
        nodes.forEach(function (n) {
          if (n && n.id) _fetchCache[_numericId(n.id)] = _normaliseVariantNode(n);
        });
      })
      .catch(function (e) {
        // Deliberately NOT cached — the next cart:updated retries.
        console.error('[StoreAvail] Batch fetch failed:', e);
      });
  }

  /* ─── Fetch storeAvailability via Storefront GraphQL ─────────────────────── */
  function _fetchStores(variantId) {
    var key = String(variantId);
    if (_fetchCache[key]) return Promise.resolve(_fetchCache[key]);

    if (!_storefrontToken) {
      console.error('[StoreAvail] No storefront token');
      return Promise.resolve(_emptyResult());
    }

    var variantGid = 'gid://shopify/ProductVariant/' + variantId;
    var query = [
      'query($id: ID!) {',
      '  node(id: $id) {',
      '    ... on ProductVariant {',
      '      availableForSale',
      '      quantityAvailable',
      '      storeAvailability(first: 50) {',
      '        edges { node {',
      '          available',
      '          quantityAvailable',
      '          location { id name address { address1 city phone } }',
      '        } }',
      '      }',
      '    }',
      '  }',
      '}'
    ].join(' ');

    console.log('[StoreAvail] Fetching variant', variantId);

    // Relative URL: same-origin on a custom storefront domain, so no CORS
    // preflight and no reliance on the _shopDomain fallback to location.hostname.
    return fetch('/api/2024-10/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': _storefrontToken
      },
      body: JSON.stringify({ query: query, variables: { id: variantGid } })
    })
      .then(function (r) { return r.json(); })
      .then(function (result) {
        console.log('[StoreAvail] Response for variant', variantId, ':', result);

        var node = result.data && result.data.node;
        var edges = node && node.storeAvailability && node.storeAvailability.edges;

        // Cache the empty result only when the variant genuinely resolved with no
        // pickup locations. An errored/unresolved response must stay uncached so
        // the next cart:updated retries instead of locking in a false negative.
        if (!edges) {
          if (node) _fetchCache[key] = _emptyResult();
          return _emptyResult();
        }

        var nodes  = edges.map(function (edge) { return edge.node; });
        var stores = nodes.map(function (n) {
          return {
            id:        _numericId(n.location && n.location.id),
            name:      n.location.name,
            available: n.available,
            qty:       n.quantityAvailable || 0,
            address1:  (n.location.address && n.location.address.address1) || '',
            city:      (n.location.address && n.location.address.city)     || '',
            phone:     (n.location.address && n.location.address.phone) || ''
          };
        });

        var flags = _computeFulfillment(nodes, node.quantityAvailable, node.availableForSale);
        var data  = { stores: stores, online: flags.online, instore: flags.instore, ok: true };

        console.log('[StoreAvail] Variant', variantId, '→ online:', data.online, 'instore:', data.instore, 'stores:', stores);
        _fetchCache[key] = data;
        return data;
      })
      .catch(function (e) {
        console.error('[StoreAvail] Fetch error for variant', variantId, ':', e);
        return _emptyResult();
      });
  }

  /* Fallback shape when data is unavailable: online/instore null so the
     server-rendered defaults are left untouched (delivery stays available). */
  function _emptyResult() {
    return { stores: [], online: null, instore: null, ok: false };
  }

  /* ─── Remembered store ───────────────────────────────────────────────────────
     sessionStorage, not localStorage: the choice survives page navigation within
     the visit but is re-asked next session. Persisted as { id, name } rather than
     a bare name so a location rename in Admin doesn't strand the choice. All
     three wrapped because storage throws in Safari private mode / when blocked.
     Storage blocked = pickup half simply stays collapsed until the shopper picks
     a store on this page — there is no auto-resolved fallback any more. */
  function _readSaved() {
    try {
      var raw = sessionStorage.getItem(_LS_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  function _saveStore(store) {
    try {
      sessionStorage.setItem(_LS_KEY, JSON.stringify({ id: store.id, name: store.name }));
    } catch (e) { /* storage blocked — choice lives only in _activeStore */ }
  }

  function _clearSaved() {
    try { sessionStorage.removeItem(_LS_KEY); } catch (e) {}
  }

  /* Match by immutable numeric Location ID first, then by trimmed case-insensitive
     name. Name matching is the fragile path: renames, translated location names on
     the zh-TW storefront, and copy-pasted whitespace all break it. */
  function _matchStore(stores, id, name) {
    if (id) {
      var byId = stores.find(function (s) { return String(s.id) === String(id); });
      if (byId) return byId;
    }
    if (!name) return null;
    var want = String(name).trim().toLowerCase();
    return stores.find(function (s) {
      return String(s.name).trim().toLowerCase() === want;
    }) || null;
  }

  /* Shopper's own choice ONLY — the store confirmed in the drawer this session
     (or already held in _activeStore when storage is blocked). Deliberately no
     theme-default / first-store fallback: "Available for Pickup" must never
     appear for a store the shopper didn't pick. Returns null → the caller sets
     data-avail-state="no-pickup" and the pickup half collapses. */
  function _resolveStore() {
    if (!_pickupEnabled) return null;

    var stores = _collectStores(_enrichedItems);
    if (!stores.length) return null;

    var saved = _readSaved() || _activeStore;
    if (!saved) return null;

    var hit = _matchStore(stores, saved.id, saved.name);
    if (hit) return hit;

    // Location renamed/closed, or the new cart contents aren't stocked-listed at
    // that store — drop the choice rather than strand the shopper on a dead one.
    _clearSaved();
    return null;
  }

  /* Drawer pre-highlight when the shopper has no confirmed store yet: the theme
     setting (numeric Location ID or name), else the first (best-available) store.
     Highlighting a row is not the same as rendering it on the cart lines. */
  function _defaultStoreIndex(stores) {
    if (!_defaultStoreRaw) return 0;
    var isNumeric = /^\d+$/.test(_defaultStoreRaw);
    var def = _matchStore(
      stores,
      isNumeric ? _defaultStoreRaw : null,
      isNumeric ? null : _defaultStoreRaw
    );
    if (def) return stores.indexOf(def);
    console.warn(
      '[StoreAvail] Default pickup store "' + _defaultStoreRaw + '" not found among',
      stores.map(function (s) { return s.name; })
    );
    return 0;
  }

  /* Rebuild _enrichedItems from whatever is currently in _fetchCache. Mirrors the
     mapping _loadAndRender does, so _collectStores and _updateCartItems can be
     reused unchanged outside the drawer-open flow. */
  function _buildEnrichedItems() {
    var byVariant = {};
    _cartItems.forEach(function (item) { byVariant[String(item.variantId)] = item; });

    _enrichedItems = _workingVariantIds().map(function (id) {
      var data = _fetchCache[id] || _emptyResult();
      var base = byVariant[id] || { variantId: id };
      return Object.assign({}, base, { variantId: id, stores: data.stores });
    });
    return _enrichedItems;
  }

  /* ─── Scroll lock ─────────────────────────────────────────────────────────────
     overflow:hidden holds on desktop but not against touch scrolling in iOS
     Safari, so on coarse pointers touchmove outside the drawer is cancelled
     outright — the drawer's own list keeps scrolling, and its
     overscroll-behavior:contain stops the gesture chaining out. Anything that
     can't be cancelled is snapped back to the offset the drawer opened at. */
  var _lockY       = 0;
  var _touchLocked = false;

  function _blockTouchMove(e) {
    var t = e.target;
    if (t && t.closest && t.closest('#store-avail-drawer')) return;
    if (e.cancelable) e.preventDefault();
  }

  function _snapDocScroll() {
    if (window.scrollY === _lockY) return;
    // Instant — the theme's html{scroll-behavior:smooth} would animate this.
    var docEl = document.documentElement;
    docEl.style.scrollBehavior = 'auto';
    window.scrollTo(0, _lockY);
    docEl.style.scrollBehavior = '';
  }

  function _lockScroll() {
    _lockY = window.scrollY;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    _touchLocked = window.matchMedia('(pointer: coarse)').matches;
    if (!_touchLocked) return;
    document.addEventListener('touchmove', _blockTouchMove, { passive: false });
    window.addEventListener('scroll', _snapDocScroll);
  }

  function _unlockScroll() {
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    if (!_touchLocked) return;
    document.removeEventListener('touchmove', _blockTouchMove, { passive: false });
    window.removeEventListener('scroll', _snapDocScroll);
    _touchLocked = false;
  }

  /* ─── Drawer open / close ────────────────────────────────────────────────── */
  function open() {
    var drawer  = document.getElementById('store-avail-drawer');
    var overlay = document.getElementById('store-avail-overlay');
    if (!drawer || !overlay) return;
    _storeConfirmed = false;
    drawer.classList.add('is-open');
    overlay.classList.add('is-open');
    _lockScroll();
    _loadAndRender();
  }

  function close() {
    var drawer  = document.getElementById('store-avail-drawer');
    var overlay = document.getElementById('store-avail-overlay');
    if (drawer)  drawer.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-open');
    _unlockScroll();
    // Only hide when there is genuinely nothing to show. Once _activeStore is
    // resolved the strip renders on load, so dismissing the drawer without
    // confirming must not wipe it.
    if (!_storeConfirmed && !_activeStore) {
      document.querySelectorAll('[data-pickup-row], [data-pickup-location-row]').forEach(function (el) {
        el.style.display = 'none';
      });
    }
  }

  /* ─── Fetch all variants then render ─────────────────────────────────────── */
  function _loadAndRender() {
    var list = document.getElementById('store-avail-list');
    if (!list) return;

    _selectedStore = null;
    var confirmBtn = document.getElementById('store-avail-confirm');
    if (confirmBtn) confirmBtn.disabled = true;

    list.innerHTML = '<li class="store-avail-drawer__loading">' + _esc(_strLoading) + '</li>';

    // Warm the cache with one batched request, then top up anything the batch
    // didn't resolve. Iterating the working ids (not _cartItems) matters after a
    // /cart.js reload, which reintroduces bundle children that have no row.
    var ids = _workingVariantIds();
    _fetchAllStores(ids).then(function () {
      return Promise.all(ids.map(function (id) { return _fetchStores(id); }));
    }).then(function () {
      _buildEnrichedItems();
      console.log('[StoreAvail] All enriched items:', _enrichedItems);
      _render(list, _enrichedItems);
    });
  }

  /* ─── Populate the per-item availability strip on the cart page ────────────
     Runs on load and after every cart mutation. Delivery reflects Online
     (shipping) availability, computed the same way as the PDP. Pickup + store
     name render immediately against the resolved store (saved choice → theme
     default → first available), no drawer interaction required. */
  function _hydrateCartAvailability() {
    var ids = _workingVariantIds();
    if (!ids.length) return;

    // No token / feature off: settle the strips into a terminal state rather
    // than leaving them stuck in `pending` (which is invisible-but-spaced).
    if (!_storefrontToken || !_pickupEnabled) {
      _setAvailState('no-pickup');
      return;
    }

    _fetchAllStores(ids).then(function () {
      _buildEnrichedItems();

      ids.forEach(function (id) {
        _applyDelivery(id, _fetchCache[id] || _emptyResult());
      });

      _activeStore = _resolveStore();
      if (_activeStore) {
        _updateCartItems(_activeStore);
        _setAvailState('ready');
      } else {
        _setAvailState('no-pickup');
      }
    });
  }

  /* Drives the CSS reveal — see .cart-item-availability[data-avail-state] in
     html/assets/sass/cart.scss. */
  function _setAvailState(state) {
    document.querySelectorAll('.cart-page-items .cart-item-availability')
      .forEach(function (el) { el.setAttribute('data-avail-state', state); });
  }

  function _applyDelivery(variantId, data) {
    if (data.online === null) return; // no data — keep the server default
    var rows = document.querySelectorAll(
      '.cart-page-items .cart-item[data-variant-id="' + variantId + '"]'
    );
    rows.forEach(function (cartItem) {
      var dIcon = cartItem.querySelector('[data-delivery-icon]');
      var dText = cartItem.querySelector('[data-delivery-avail]');
      if (dText) dText.textContent = data.online ? _strAvailableDelivery : _strNotAvailableDelivery;
      if (dIcon) dIcon.innerHTML = data.online ? _iconAvailable : _iconUnavailable;
    });
  }

  /* ─── Build union of all store locations with availability ───────────────── */
  function _collectStores(enrichedItems) {
    var seen = {};
    var stores = [];
    enrichedItems.forEach(function (item) {
      (item.stores || []).forEach(function (s) {
        if (!seen[s.name]) {
          seen[s.name] = true;
          // Carry the numeric Location id through — _matchStore prefers it over
          // the rename-fragile name when restoring the session's saved choice.
          stores.push({ id: s.id, name: s.name, address1: s.address1, city: s.city, phone: s.phone, allAvailable: true });
        }
      });
    });
    stores.forEach(function (store) {
      enrichedItems.forEach(function (item) {
        var entry = (item.stores || []).find(function (s) { return s.name === store.name; });
        if (!_isAvailableAt(entry)) store.allAvailable = false;
      });
    });
    // Available stores first (stable — keeps original order within each group).
    stores.sort(function (a, b) { return (b.allAvailable ? 1 : 0) - (a.allAvailable ? 1 : 0); });
    return stores;
  }

  /* ─── Render store list ──────────────────────────────────────────────────── */
  function _render(list, enrichedItems) {
    list.innerHTML = '';

    var stores = _collectStores(enrichedItems);

    if (!stores.length) {
      var empty = document.createElement('li');
      empty.className   = 'store-avail-drawer__empty';
      empty.textContent = _strNoStores;
      list.appendChild(empty);
      return;
    }

    // Highlight the store already shown on the cart rows so the drawer agrees
    // with the strip; with no confirmed store yet, fall back to the theme
    // default (else the first, best-available store).
    var preselect = _defaultStoreIndex(stores);
    if (_activeStore) {
      var found = stores.findIndex(function (s) { return s.name === _activeStore.name; });
      if (found > -1) preselect = found;
    }

    stores.forEach(function (store, index) {
      var addressLine  = [store.address1, store.city].filter(Boolean).join(', ');
      var icon         = store.allAvailable ? _iconAvailable : _iconUnavailable;
      var statusText   = store.allAvailable ? _strAllAvailable : _strSomeUnavailable;

      var li = document.createElement('li');
      li.className = 'store-avail-drawer__store-item';
      if (index === preselect) {
        li.classList.add('is-selected');
        _selectedStore = store;
      }
      li.innerHTML =
        '<div class="store-avail-drawer__store-card">' +
          '<div class="store-avail-drawer__store-info">' +
            '<p class="store-avail-drawer__store-name">' + _esc(store.name) + '</p>' +
            (addressLine ? '<span class="store-avail-drawer__store-address">' + _esc(addressLine) + '</span>' : '') +
            (store.phone ? '<span class="store-avail-drawer__store-phone">'   + _esc(store.phone) + '</span>' : '') +
          '</div>' +
          '<div class="store-avail-drawer__store-status">' +
            '<span class="store-avail-drawer__status-icon">' + icon + '</span>' +
            '<span class="store-avail-drawer__status-text">' + _esc(statusText) + '</span>' +
          '</div>' +
        '</div>';

      li.addEventListener('click', function () {
        list.querySelectorAll('.store-avail-drawer__store-item').forEach(function (el) {
          el.classList.remove('is-selected');
        });
        li.classList.add('is-selected');
        _selectedStore = store;
        var btn = document.getElementById('store-avail-confirm');
        if (btn) btn.disabled = false;
        console.log('[StoreAvail] Selected:', store.name);
      });

      list.appendChild(li);
    });

    /* Enable button since first store is auto-selected; clone removes stale listener */
    var confirmBtn = document.getElementById('store-avail-confirm');
    if (confirmBtn) {
      var fresh = confirmBtn.cloneNode(true);
      fresh.disabled = false;
      confirmBtn.parentNode.replaceChild(fresh, confirmBtn);
      fresh.addEventListener('click', function () {
        if (!_selectedStore) return;
        console.log('[StoreAvail] Confirmed:', _selectedStore.name);
        _storeConfirmed = true;
        _saveStore(_selectedStore);
        _activeStore = _selectedStore;
        _updateCartItems(_selectedStore);
        _setAvailState('ready');
        close();
      });
    }
  }

  /* ─── Update pickup line in each cart item ───────────────────────────────── */
  function _updateCartItems(store) {
    document.querySelectorAll('.cart-page-items .cart-item[data-variant-id]').forEach(function (cartItem) {
      var variantId = cartItem.dataset.variantId;
      var pickupEl  = cartItem.querySelector('[data-pickup-avail]');
      var iconEl    = cartItem.querySelector('[data-pickup-icon]');
      var rowEl     = cartItem.querySelector('[data-pickup-row]');
      var locRowEl  = cartItem.querySelector('[data-pickup-location-row]');
      var locEl     = cartItem.querySelector('[data-pickup-location]');
      if (!pickupEl) return;

      var enriched = _enrichedItems.find(function (item) {
        return String(item.variantId) === String(variantId);
      });

      var storeEntry = enriched
        ? (enriched.stores || []).find(function (s) { return s.name === store.name; })
        : null;
      var available = _isAvailableAt(storeEntry);

      console.log('[StoreAvail] variant', variantId, '@', store.name, '→ available:', available);

      // Store name is shown in BOTH states — an item that can't be picked up
      // still needs to say which store it can't be picked up from.
      // textContent, never innerHTML: the name comes from the Storefront API.
      if (locEl) locEl.textContent = store.name;
      if (locRowEl) locRowEl.style.display = '';

      pickupEl.textContent = available ? _strAvailablePickup : _strNotAvailablePickup;
      if (iconEl) iconEl.innerHTML = available ? _iconAvailable : _iconUnavailable;
      if (rowEl) {
        rowEl.style.display = '';
        rowEl.dataset.pickupState = available ? 'yes' : 'no';
      }
    });
  }

  /* ─── Reload cart items from live /cart.js (after AJAX mutations) ───────── */
  function _reloadCartItems() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        _cartItems = cart.items.map(function (item) {
          return {
            handle:       item.handle,
            variantId:    item.variant_id,
            title:        item.product_title,
            variantTitle: item.variant_title,
            image:        item.image || ''
          };
        });
        // _fetchCache is deliberately NOT cleared: store stock doesn't change
        // because the shopper changed a quantity, and _fetchAllStores only
        // requests ids it hasn't seen. Stale entries for removed variants are
        // harmless — _collectStores reads _enrichedItems, not the cache.
        var drawer = document.getElementById('store-avail-drawer');
        if (drawer && drawer.classList.contains('is-open')) {
          _loadAndRender();
        }
        // Refresh the per-item Delivery/Pickup indicators for the new line items.
        _hydrateCartAvailability();
      })
      .catch(function (e) {
        console.error('[StoreAvail] Failed to reload cart items:', e);
      });
  }

  /* ─── Boot ───────────────────────────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', function () {
    _loadCartItems();
    _hydrateCartAvailability();
  });
  /* cart:updated fires TWICE per mutation — once from cart-layout.liquid's
     updateCartUI and once from cart-items.js after its own /cart.js reload.
     Debounce so one mutation costs one reload, not two. */
  document.addEventListener('cart:updated', function () {
    clearTimeout(_updateTimer);
    _updateTimer = setTimeout(_reloadCartItems, 60);
  });

  global.CartStoreAvail       = { open: open, close: close };
  global.openStoreAvailDrawer  = open;
  global.closeStoreAvailDrawer = close;

})(window);

